import { useState } from "react"
import { gql, useLazyQuery } from "@apollo/client"
import { DateTime } from "luxon"
import { LogDebug, LogError } from "./logger"
import _ from "lodash"
import { transformDate } from "./dateNormalization"

// Message constants
const MANAGE_SERVICES_CUSTOM_MESSAGE_RANGE =
  "Manage Services must be dated within the active Service Episode: @minDate to @maxDate"
const DISCHARGE_SESSION_CUSTOM_MESSAGE_RANGE =
  "Discharge must be dated between the last activity in the current Service Episode and today: @minDate to @maxDate"
const INTAKE_SESSION_CUSTOM_MESSAGE_RANGE =
  "Participant engagements limit date selection between @minDate and @maxDate"
const INTAKE_SESSION_CUSTOM_MESSAGE_MAX_ONLY =
  "Participant engagements limit date selection up to @maxDate"

// This object can be extended to define additional fields or field + template combos
const VALIDATION_CONFIG = [
  {
    field: "manage_services_date",
    template: "manage_services",
    customMessages: {
      new: {
        range: MANAGE_SERVICES_CUSTOM_MESSAGE_RANGE,
        maxOnly: null, // Not applicable based on the rules
      },
      edit: {
        range: null, // Not applicable based on the rules
        maxOnly: null, // Not applicable based on the rules
      },
    },
  },
  {
    field: "session_date",
    template: "discharge_session",
    customMessages: {
      new: {
        range: DISCHARGE_SESSION_CUSTOM_MESSAGE_RANGE,
        maxOnly: null, // Not applicable based on the rules
      },
      edit: {
        range: null, // Not applicable because discharge session edit validation is not yet supported
        maxOnly: null, // Not applicable because discharge session edit validation is not yet supported
      },
    },
  },
  {
    field: "session_date",
    template: "intake_session",
    customMessages: {
      new: {
        range: null, // Not applicable because intake_session new validation is excluded
        maxOnly: null, // Not applicable because intake_session new validation is excluded
      },
      edit: {
        range: INTAKE_SESSION_CUSTOM_MESSAGE_RANGE,
        maxOnly: INTAKE_SESSION_CUSTOM_MESSAGE_MAX_ONLY,
      },
    },
  },
  {
    field: "session_date",
    template: "intake_reactivation_session",
    customMessages: {
      new: {
        range: INTAKE_SESSION_CUSTOM_MESSAGE_RANGE,
        maxOnly: INTAKE_SESSION_CUSTOM_MESSAGE_MAX_ONLY,
      },
      edit: {
        range: INTAKE_SESSION_CUSTOM_MESSAGE_RANGE,
        maxOnly: INTAKE_SESSION_CUSTOM_MESSAGE_MAX_ONLY,
      },
    },
  },
]

const GET_PARTICIPANT_SERVICE_DATES = gql`
  query GetParticipantServiceDates($participant_membership_id: ID!) {
    serviceDateValidationBounds(
      participant_membership_id: $participant_membership_id
    ) {
      previousEpisodeEndDate
      currentEpisodeStartDate
      firstServiceDateInCurrentEpisode
      lastServiceDateInCurrentEpisode
    }
  }
`

const SERVICE_EPISODE_TEMPLATES = [
  "intake_session",
  "intake_reactivation_session",
  "discharge_session",
]

export const helperStates = {
  IDLE: "idle",
  INITIALIZING: "initializing",
  READY: "ready",
  ERROR: "error",
}

const isServiceEpisode = (idTemplate) => {
  return SERVICE_EPISODE_TEMPLATES.includes(idTemplate)
}

const isDischargeSession = (idTemplate) => {
  return idTemplate === "discharge_session"
}

const isValidationField = (fieldName, idTemplate, isEdit) => {

  // Hardcoded exclusions
  if(idTemplate === "intake_session" && !isEdit) {
    // Exclude intake_session for new records (only validate on edit)
    return false
  }
  
  return VALIDATION_CONFIG.some(
    (validationField) =>
      validationField.field === fieldName &&
      (!validationField.template || validationField.template === idTemplate),
  )
}

const transformServiceDate = (value) => {
  const transformedDate = transformDate(value, "date", "date", true)
  return transformedDate !== "" ? transformedDate : null
}

const getValidationMessage = (
  fieldName,
  idTemplate,
  minDate,
  maxDate,
  isEdit = false,
) => {
  // Check for a custom validation message for this specific field + template combo (or field alone)
  const validationField = VALIDATION_CONFIG.find(
    (vf) =>
      vf.field === fieldName && (!vf.template || vf.template === idTemplate),
  )
  const hasMinDate = !!minDate?.isValid
  const hasMaxDate = !!maxDate?.isValid
  const modeKey = isEdit ? "edit" : "new"

  let boundsKey = null
  if (hasMinDate && hasMaxDate) {
    boundsKey = "range"
  } else if (!hasMinDate && hasMaxDate) {
    boundsKey = "maxOnly"
  }

  // Retrieve the custom message for this mode (new vs edit) if it exists
  const modeMessages = validationField?.customMessages?.[modeKey]

  // If we have a valid boundsKey (range or maxOnly) and a custom message defined for this scenario, use it.
  // Otherwise, we'll fall back to default messages.
  const customMessage =
    boundsKey && modeMessages ? modeMessages[boundsKey] : null

  if (boundsKey && !customMessage) {
    LogDebug(
      `No custom validation message configured for field "${fieldName}", template "${idTemplate}", mode "${modeKey}", and bounds "${boundsKey}". Falling back to default message.`,
    )
    // Fallback to default message if specific custom message is not configured for this bounds scenario (range, maxOnly)
  }

  // If a custom message is defined, replace placeholders with actual dates and return it
  if (customMessage) {
    if (!hasMinDate && customMessage.includes("@minDate")) {
      LogDebug(
        `Custom validation message for field "${fieldName}" and template "${idTemplate}" includes a @minDate placeholder but no minDate was provided. Please check your VALIDATION_CONFIG configuration.`,
      )
      // Fallback to default message if placeholders are misconfigured
    } else if (!hasMaxDate && customMessage.includes("@maxDate")) {
      LogDebug(
        `Custom validation message for field "${fieldName}" and template "${idTemplate}" includes a @maxDate placeholder but no maxDate was provided. Please check your VALIDATION_CONFIG configuration.`,
      )
      // Fallback to default message if placeholders are misconfigured
    } else {
      // Replace placeholders and return custom message
      return customMessage
        .replaceAll("@minDate", hasMinDate ? minDate.toFormat("M/d/yyyy") : "")
        .replaceAll("@maxDate", hasMaxDate ? maxDate.toFormat("M/d/yyyy") : "")
    }
  }

  // Return default messages if no custom message is defined
  if (!hasMinDate && hasMaxDate) {
    // If no minDate, only replace maxDate placeholder
    return `Date must be on or before ${maxDate.toFormat("M/d/yyyy")}.`
  }
  // If both minDate and maxDate are present, replace both placeholders
  if (hasMinDate && hasMaxDate) {
    return `Date must be between ${minDate.toFormat("M/d/yyyy")} and ${maxDate.toFormat("M/d/yyyy")}.`
  }
}

// Helper function that checks if the parsedCheckDate falls within the
// provided bounds and returns an appropriate error message when it doesn't.
const validateCheckDate = (
  fieldName,
  idTemplate,
  parsedCheckDate,
  validationMinDate,
  validationMaxDate,
  isEdit,
) => {
  const checkDateStart = parsedCheckDate.startOf("day")
  const minDateStart = validationMinDate?.isValid
    ? validationMinDate.startOf("day")
    : null

  // Max always has an effective value (fallback to "now" to block future dates)
  const effectiveMaxDate = validationMaxDate?.isValid
    ? validationMaxDate.startOf("day")
    : DateTime.now().startOf("day")

  // Only enforce min when min exists
  const isBeforeMin = minDateStart && checkDateStart < minDateStart

  // Always enforce max (actual max or fallback max)
  const isAfterMax = checkDateStart > effectiveMaxDate

  // Valid date
  if (!isBeforeMin && !isAfterMax) {
    return undefined
  }

  // Missing min-date means "only upper-bound validation"
  // Both bounds present (or min present + max fallback)
  return getValidationMessage(
    fieldName,
    idTemplate,
    minDateStart,
    effectiveMaxDate,
    isEdit,
  )
}

export function useServiceDateValidation() {
  const [serviceDates, setServiceDates] = useState({})
  const [status, setStatus] = useState(helperStates.IDLE)
  const [initError, setInitError] = useState(null)
  const [idTemplate, setIdTemplate] = useState(null)
  const [fieldName, setFieldName] = useState(null)
  const [isEdit, setIsEdit] = useState(false)
  const [forceValidation, setForceValidation] = useState(false)

  const [fetchParticipantServiceDates] = useLazyQuery(
    GET_PARTICIPANT_SERVICE_DATES,
    {
      fetchPolicy: "no-cache", // Ensure we always have the latest data for validation
    },
  )

  async function initializeServiceDateValidation({
    participant_membership_id,
    idTemplate,
    field_name,
    isEdit = false,
    forceValidation = false,
  }) {
    try {
      setIdTemplate(idTemplate) // Store for later use
      setFieldName(field_name) // Store for later use
      setIsEdit(isEdit) // Store for later use
      setForceValidation(forceValidation) // Store for later use

      // Skip initialization (and validation) if:
      // - If the field being validated isn't one of the expected service date fields
      if (!forceValidation && !isValidationField(field_name, idTemplate, isEdit)) {
        LogDebug(
          "serviceDateValidationHelper: This date field is not configured for validation, skipping service date validation",
          {
            field_name,
            idTemplate,
          },
        )
        setStatus(helperStates.READY)
        return
      }

      setStatus(helperStates.INITIALIZING)
      setInitError(null)

      // Ensure participant_membership_id is provided
      if (!participant_membership_id) {
        setStatus(helperStates.ERROR)
        setInitError("Missing participant membership ID.")
        return
      }

      const serviceDateValidationBoundsRes = await fetchParticipantServiceDates(
        {
          variables: {
            participant_membership_id,
          },
        },
      )

      if (serviceDateValidationBoundsRes.error) {
        throw new Error(
          serviceDateValidationBoundsRes.error.message || "Unknown error",
        )
      } else if (
        !serviceDateValidationBoundsRes.data?.serviceDateValidationBounds
      ) {
        setStatus(helperStates.ERROR)
        setInitError("Unable to load participant service dates for validation.")
        return
      }

      const _serviceDates = {
        previousEpisodeEndDate: transformServiceDate(
          serviceDateValidationBoundsRes?.data?.serviceDateValidationBounds
            ?.previousEpisodeEndDate,
        ),
        currentEpisodeStartDate: transformServiceDate(
          serviceDateValidationBoundsRes?.data?.serviceDateValidationBounds
            ?.currentEpisodeStartDate,
        ),
        firstServiceDateInCurrentEpisode: transformServiceDate(
          serviceDateValidationBoundsRes?.data?.serviceDateValidationBounds
            ?.firstServiceDateInCurrentEpisode,
        ),
        lastServiceDateInCurrentEpisode: transformServiceDate(
          serviceDateValidationBoundsRes?.data?.serviceDateValidationBounds
            ?.lastServiceDateInCurrentEpisode,
        ),
      }

      LogDebug("Service date validation bounds fetched:", _serviceDates)

      setServiceDates(_serviceDates)
      setStatus(helperStates.READY)
    } catch (error) {
      setStatus(helperStates.ERROR)
      const errorMessage =
        "An unexpected error occurred while initializing service date validation."
      setInitError(errorMessage)
      LogError(errorMessage, error)
    }
  }

  function checkServiceDate({
    checkDate, // The date value to validate. We support JS Date and Luxon DateTime objects
  }) {
    try {
      // Skip validation:
      // - If checkDate is missing
      // - If the field being validated isn't one of the expected service date fields
      if (
        !checkDate ||
        (!forceValidation && !isValidationField(fieldName, idTemplate, isEdit))
      ) {
        return undefined
      }

      // Ensure idTemplate is provided
      if (!idTemplate) {
        throw new Error("Missing idTemplate in checkServiceDate args")
      }

      if (status === helperStates.INITIALIZING) {
        return "Loading, please wait..."
      }

      if (status !== helperStates.READY) {
        throw new Error(
          `Service date validation is not ready. Current status: ${status}. Init error: ${initError || "Unknown error"}`,
        )
      }

      // Parse the checkDate into a Luxon DateTime for consistent comparison later
      let parsedCheckDate
      if (_.isDate(checkDate)) {
        parsedCheckDate = DateTime.fromJSDate(checkDate)
      } else if (DateTime.isDateTime(checkDate)) {
        parsedCheckDate = checkDate
      } else {
        throw new Error(
          "Invalid checkDate provided. Must be a JS Date or Luxon DateTime object.",
        )
      }

      let validationMinDate, validationMaxDate
      if (!isServiceEpisode(idTemplate)) {
        // --------------------------------------------
        // PROCESS NON-EPISODE SERVICE DATE VALIDATION
        // (ENGAGEMENTS, SERVICES, DATA FORMS)
        // --------------------------------------------

        // Ensure we have the necessary service date bounds for validation
        if (!serviceDates.currentEpisodeStartDate) {
          return "No active service episode found for this participant."
        }

        // Rules:
        // - parsedCheckDate must be AFTER the active episode start date (i.e. the session can't be dated before the episode starts)
        // - parsedCheckDate must be BEFORE the current date (i.e. the session can't be dated in the future)
        validationMinDate = serviceDates.currentEpisodeStartDate
        validationMaxDate = DateTime.now()
      } else {
        // -----------------------------------------
        // PROCESS SERVICE EPISODE VALIDATION
        // (INTAKE/REACTIVATION/DISCHARGE SESSIONS)
        // -----------------------------------------

        if (!isEdit) {
          if (isDischargeSession(idTemplate)) {
            // Rules for new discharge session validation:
            // - parsedCheckDate must be AFTER any services provided within the active service episode,
            //     or if no services have been provided, after the active episode start date
            // - parsedCheckDate must be BEFORE the current date (i.e. the session can't be dated in the future)
            validationMinDate =
              serviceDates.lastServiceDateInCurrentEpisode ||
              serviceDates.currentEpisodeStartDate
            validationMaxDate = DateTime.now()
          } else {
            // Rules for new service episode (intake/reactivation) validation:
            // - parsedCheckDate must be AFTER the most recent past episode discharge date (if one exists)
            // - parsedCheckDate must be BEFORE the current date (i.e. the session can't be dated in the future)
            validationMinDate = serviceDates.previousEpisodeEndDate
            validationMaxDate = DateTime.now()
          }
        } else {
          if (isDischargeSession(idTemplate)) {
            // Rules for discharge session edit validation:
            // This feature is not yet available.
            return "Editing discharge session dates is not currently supported. Please contact support if you need to update this date."
          } else {
            // Rules for edit service episode (intake/reactivation) validation:
            // - parsedCheckDate must be AFTER the most recent past episode discharge date (if one exists)
            // - parsedCheckDate must be BEFORE any services provided within the active service episode
            validationMinDate = serviceDates.previousEpisodeEndDate
            validationMaxDate = serviceDates.firstServiceDateInCurrentEpisode
          }
        }
      }

      // Perform the actual validation check and return any error message (or undefined if valid)
      return validateCheckDate(
        fieldName,
        idTemplate,
        parsedCheckDate,
        validationMinDate,
        validationMaxDate,
        isEdit,
      )
    } catch (error) {
      LogError("Error in useServiceDateValidation:", error)
      return "Date validation is unavailable. Please try again later."
    }
  }

  return {
    status,
    initError,
    initializeServiceDateValidation,
    checkServiceDate,
  }
}
